import { IMinMaxDefault, ITargetInfo, UserTargetTypes } from 'fitbeat.models';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { translate } from 'react-i18next';
import {
  StyleSheet, Text, View, Platform,
} from 'react-native';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import Theme from '../../theme';
import { SliderGauge } from '../../ui';
import { defaultScale, scaleText } from '../../utils/scaling';
import { WeightTargetHeader } from './weight-target-header';
import { WeightTargetRangeInformation } from './weight-target-range-information';

interface IProps {
  t: any,
  minMaxDefault: IMinMaxDefault,
  targetInfo: ITargetInfo;
  isApplying: boolean;
  isCalculatingTarget: boolean;
  userTargetType: UserTargetTypes;
  isChangingTarget: boolean;
  onTargetWeightChange: (roundedValue: number, isCurrentMetric: boolean) => void;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  goToFaq: () => void;
}

const WeightTargetAdjustComponent:FunctionComponent<IProps> = ({
  t, minMaxDefault, targetInfo, goToPreviousStep,
  goToNextStep, goToFaq, isApplying, isCalculatingTarget,
  onTargetWeightChange, userTargetType, isChangingTarget,
}) => {
  const [target, setTarget] = useState(minMaxDefault.default);
  const [isCurrentMetric, setIsCurrentMetric] = useState(false);
  const stepForTargetSlider = 0.02;
  const currentMetricTitle = new Map([
    [UserTargetTypes.Musclemass, t('currentMetric', { metric: 'muscle mass' })],
    [UserTargetTypes.Bodyweight, t('currentMetric', { metric: 'weight' })],
    [UserTargetTypes.Bodyfat, t('currentMetric', { metric: 'body fat' })],
  ]).get(userTargetType);

  const targetValueTitle = new Map([
    [UserTargetTypes.Musclemass, t('targetMetric', { metric: 'muscle mass' })],
    [UserTargetTypes.Bodyweight, t('targetMetric', { metric: 'weight' })],
    [UserTargetTypes.Bodyfat, t('targetMetric', { metric: 'body fat' })],
  ]).get(userTargetType);

  const title = new Map([
    [UserTargetTypes.Musclemass, t('targetQuestion', { targetType: 'muscle mass' })],
    [UserTargetTypes.Bodyweight, t('targetQuestion', { targetType: 'bodyweight' })],
    [UserTargetTypes.Bodyfat, t('targetQuestion', { targetType: 'body fat' })],
  ]).get(userTargetType);
  const subTitle = new Map([
    [UserTargetTypes.Musclemass, t('targetDescription', { targetType: 'muscle mass' })],
    [UserTargetTypes.Bodyweight, t('targetDescription', { targetType: 'bodyweight' })],
    [UserTargetTypes.Bodyfat, t('targetDescription', { targetType: 'body fat' })],
  ]).get(userTargetType);

  useEffect(() => {
    const isCurrentMetric = userTargetType === UserTargetTypes.Musclemass
      ? minMaxDefault.default === minMaxDefault.min
      : minMaxDefault.default === minMaxDefault.max;
    setIsCurrentMetric(isCurrentMetric);
    setTarget(minMaxDefault.default);
  }, [minMaxDefault.default]);

  const weightChangeCalc = () => {
    if (!targetInfo) { return 0; }
    if (isCalculatingTarget) { return ' '; }

    let change = 0;

    switch (userTargetType) {
      case UserTargetTypes.Musclemass:
        change = targetInfo.muscleMassChange!;
        break;
      case UserTargetTypes.Bodyweight:
        change = targetInfo.weightChange!;
        break;
      case UserTargetTypes.Bodyfat:
        change = targetInfo.bodyFatChange!;
        break;
    }

    if (change === 0) {
      return `${Math.abs(change)} ${targetInfo.unit}`;
    }

    const formattedChange = Math.abs(change);
    const roundedChange = Math.round(formattedChange * 10) / 10;

    return change < 0
      ? `lose ${roundedChange} ${targetInfo.unit}`
      : `gain ${roundedChange} ${targetInfo.unit}`;
  };

  const shouldButtonBeDisabled = () => {
    if (userTargetType === UserTargetTypes.Musclemass) {
      const isTargetSameAsMin = target === minMaxDefault.min || minMaxDefault.max === minMaxDefault.min;
      return isTargetSameAsMin;
    }
    const isTargetSameAsMax = target === minMaxDefault.max || minMaxDefault.max === minMaxDefault.min;
    return isTargetSameAsMax;
  };

  const renderTarget = () => (isCurrentMetric
    ? (
      <Text style={styles.noTargetIndicator}>-</Text>
    )
    : (
      <>
        <Text style={styles.targetValueStyle}>
          {target}
          {targetInfo.unit}
        </Text>
        <Text style={styles.targetDescripStyle}>{targetInfo && weightChangeCalc()}</Text>
      </>
    )
  );

  const getTargetDateStyling = () => {
    const marginTop = userTargetType === UserTargetTypes.Musclemass ? defaultScale(20) : 0;

    return {
      ...styles.noTargetIndicator,
      marginTop,
    };
  };

  const renderTargetDate = () => (isCurrentMetric ? (
    <Text
      style={getTargetDateStyling()}
    >
      -
    </Text>
  ) : (
    <>
      <Text
        style={getTargetDateStyling()}
      >
        {targetInfo && targetInfo.shortDate}
      </Text>
      <Text style={styles.targetDescripStyle}>
        {targetInfo && targetInfo.duration}
      </Text>
    </>
  ));

  return (
    <View style={styles.flex}>
      <WeightTargetHeader
        title={title}
        subTitle={subTitle}
        goToPreviousStep={goToPreviousStep}
        showCloseButton={isChangingTarget}
      />
      <View style={styles.container}>

        <View style={styles.targetContainer}>
          <View style={styles.targetDescription}>
            <Text style={styles.targetValueTitle}>{targetValueTitle}</Text>
            {!targetInfo
              ? (
                <>
                  <Text style={styles.targetValueStyle}>...</Text>
                  <Text style={styles.targetDescripStyle}> </Text>
                </>
              )
              : renderTarget()}
          </View>
          <View style={styles.targetDescription}>
            <Text style={styles.targetValueTitle}>Target date</Text>
            {!targetInfo || isCalculatingTarget
              ? <Text style={styles.targetValueStyle}>...</Text>
              : renderTargetDate()}
          </View>
          <View style={styles.targetDescription}>
            <Text style={styles.targetValueTitle}>{currentMetricTitle}</Text>
            {!targetInfo || isCalculatingTarget ? (
              <Text style={styles.targetValueStyle}>...</Text>
            ) : (
              <Text style={styles.targetValueStyle}>
                {userTargetType === UserTargetTypes.Musclemass
                  ? minMaxDefault.min
                  : minMaxDefault.max}
                {targetInfo.unit}
              </Text>
            )}
          </View>
        </View>
        <View>
          <SliderGauge
            style={styles.sliderStyle}
            inverted={false}
            onSlidingComplete={(value) => {
              const roundedValue = Math.round(value * 10) / 10;
              const isCurrentMetric = userTargetType === UserTargetTypes.Musclemass
                ? roundedValue === minMaxDefault.min
                : roundedValue === minMaxDefault.max;
              setIsCurrentMetric(isCurrentMetric);
              setTarget(roundedValue);
              onTargetWeightChange(value, isCurrentMetric);
            }}
            initialValue={minMaxDefault.default}
            minimumValue={minMaxDefault.min}
            maximumValue={minMaxDefault.max}
            step={stepForTargetSlider}
            minimumTrackTintColor={userTargetType === UserTargetTypes.Musclemass ? Theme.PRIMARY : Theme.GREY}
            maximumTrackTintColor={userTargetType === UserTargetTypes.Musclemass ? Theme.GREY : Theme.PRIMARY}
          />
        </View>

        <WeightTargetRangeInformation minMaxDefault={minMaxDefault} t={t} target={target} targetType={userTargetType} />

        <View style={[styles.flex, { flexGrow: 1 }]} />

        <View style={styles.actionsContainer}>
          <Button
            title={t('learnMore')}
            onPress={goToFaq}
            fontColor={Theme.PRIMARY}
            buttonType={ButtonTypes.TRANSPARENT}
            size={ButtonSize.NORMAL}
            containerStyle={{ marginBottom: defaultScale(20) }}
          />
          <Button
            title={t('accept')}
            onPress={goToNextStep}
            fontColor={Theme.WHITE}
            buttonType={ButtonTypes.NORMAL}
            size={ButtonSize.NORMAL}
            isLoading={isApplying || isCalculatingTarget}
            disabled={shouldButtonBeDisabled()}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  targetContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    height: defaultScale(100),
  },
  targetValueTitle: {
    marginBottom: defaultScale(5),
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
    textAlign: 'center',
    width: defaultScale(120),
  },
  targetValueStyle: {
    ...Theme.graphStyle.customBold22,
    fontSize: scaleText(26),
  },
  targetDescripStyle: {
    marginTop: defaultScale(5),
    ...Theme.textStyle.small,
    color: Theme.GREY_MEDIUM,
  },
  actionsContainer: {
    marginTop: defaultScale(40),
    paddingBottom: defaultScale(40),
  },
  targetDescription: {
    alignItems: 'center',
    width: '50%',
    ...Platform.select({
      web: {
        width: '30%',
      },
    }),
  },
  sliderStyle: {
    marginTop: defaultScale(30),
  },
  flex: {
    flex: 1,
  },
  noTargetIndicator: {
    ...Theme.graphStyle.customBold22,
    fontSize: scaleText(26),
  },
});

export const WeightTargetAdjust = translate(['weightTarget'])(WeightTargetAdjustComponent);
